import {
  CityMapItem,
  ForeignScansMeta,
  LocationPoint,
  LocationPointsResponse,
  ScanLocationResponse,
} from "../types/location"
import { normalizePointHistory } from "./pointHistory"

type RawLocationPoint = Record<string, unknown>

const DEFAULT_RADIUS_M = 50

function pickNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const normalized = value.trim().replace(",", ".")
    const n = Number(normalized)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function pickBool(value: unknown, fallback = true): boolean {
  if (typeof value === "boolean") return value
  if (value === 0 || value === "0") return false
  if (value === 1 || value === "1") return true
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value.trim() || fallback
  if (value == null) return fallback
  return String(value).trim() || fallback
}

function isRawPointShape(raw: RawLocationPoint): boolean {
  const lat = pickNumber(raw.lat ?? raw.latitude)
  const lng = pickNumber(raw.lng ?? raw.longitude ?? raw.lon)
  return lat != null && lng != null
}

function unwrapRawPoint(raw: RawLocationPoint): RawLocationPoint {
  const nested = raw.point ?? raw.location ?? raw.locationPoint
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawLocationPoint), ...raw }
  }
  return raw
}

export function normalizeLocationPoint(raw: RawLocationPoint): LocationPoint | null {
  const item = unwrapRawPoint(raw)
  const id =
    item.id ??
    item.uuid ??
    item.pointId ??
    item.point_id ??
    item.locationPointId ??
    item.location_point_id

  const coords = item.coordinates
  let coordLat: number | null = null
  let coordLng: number | null = null
  if (Array.isArray(coords) && coords.length >= 2) {
    coordLng = pickNumber(coords[0])
    coordLat = pickNumber(coords[1])
  }

  const lat = pickNumber(item.lat ?? item.latitude ?? coordLat)
  const lng = pickNumber(item.lng ?? item.longitude ?? item.lon ?? coordLng)
  const radiusM =
    pickNumber(
      item.radiusM ??
        item.radiusMeters ??
        item.radius_meters ??
        item.radius ??
        item.scanRadiusMeters
    ) ?? DEFAULT_RADIUS_M

  if (id == null || lat == null || lng == null) {
    return null
  }

  const title = String(item.title ?? item.name ?? "").trim()

  return {
    id: String(id),
    title: title || `Точка ${id}`,
    lat,
    lng,
    radiusM,
    cityId: pickNumber(item.cityId ?? item.city_id) ?? undefined,
    isOwnCity: (() => {
      const raw = item.isOwnCity ?? item.is_own_city
      if (typeof raw === "boolean") return raw
      if (raw === 0 || raw === "0") return false
      if (raw === 1 || raw === "1") return true
      return undefined
    })(),
    isAvailable: pickBool(item.isAvailable ?? item.is_available, true),
    cooldownUntil:
      (item.cooldownUntil ?? item.cooldown_until ?? null) as
        | string
        | null
        | undefined,
    dominantLeague: (() => {
      const raw = pickString(
        item.dominantLeague ?? item.dominant_league
      ).toUpperCase()
      if (raw === "TITAN" || raw === "ATLANT") return raw
      if (raw === "NEUTRAL") return "NEUTRAL" as const
      return undefined
    })(),
    titanScans:
      pickNumber(item.titanScans ?? item.titan_scans) || undefined,
    atlantScans:
      pickNumber(item.atlantScans ?? item.atlant_scans) || undefined,
    history: (() => {
      const history = normalizePointHistory({ history: item.history ?? item.pointHistory })
      return history.events.length > 0 ? history : undefined
    })(),
  }
}

function collectArrays(...candidates: unknown[]): RawLocationPoint[] {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as RawLocationPoint[]
    }
  }
  return []
}

export function extractLocationPointsPayload(data: unknown): RawLocationPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return extractLocationPointsPayload(JSON.parse(data))
    } catch {
      return []
    }
  }

  if (Array.isArray(data)) return data as RawLocationPoint[]

  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>
    const nestedData =
      obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
        ? (obj.data as Record<string, unknown>)
        : null

    const fromArrays = collectArrays(
      obj.points,
      obj.items,
      obj.locations,
      obj.locationPoints,
      obj.result,
      obj.data,
      nestedData?.points,
      nestedData?.items,
      nestedData?.locations,
      nestedData?.locationPoints
    )
    if (fromArrays.length > 0) return fromArrays

    if (isRawPointShape(obj)) return [obj]
    if (nestedData && isRawPointShape(nestedData)) return [nestedData]
  }

  return []
}

export function mapLocationPoints(data: unknown): LocationPoint[] {
  return extractLocationPointsPayload(data)
    .map(normalizeLocationPoint)
    .filter((point): point is LocationPoint => point != null)
}

const DEFAULT_FOREIGN_SCANS: ForeignScansMeta = {
  today: 0,
  limit: 40,
  remaining: 40,
}

function normalizeForeignScans(raw: unknown): ForeignScansMeta {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return DEFAULT_FOREIGN_SCANS
  }
  const item = raw as Record<string, unknown>
  const limit = pickNumber(item.limit) ?? DEFAULT_FOREIGN_SCANS.limit
  const today = pickNumber(item.today) ?? 0
  const remaining =
    pickNumber(item.remaining) ?? Math.max(0, limit - today)
  return { today, limit, remaining }
}

function normalizeCityPolygon(raw: unknown): number[][] {
  if (!Array.isArray(raw)) return []
  const polygon: number[][] = []
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 2) continue
    const lat = pickNumber(item[0])
    const lng = pickNumber(item[1])
    if (lat != null && lng != null) polygon.push([lat, lng])
  }
  return polygon
}

function normalizeCityMapItem(raw: RawLocationPoint): CityMapItem | null {
  const cityId = pickNumber(raw.cityId ?? raw.city_id)
  if (cityId == null) return null
  const isOwnCityRaw = raw.isOwnCity ?? raw.is_own_city
  let isOwnCity = false
  if (typeof isOwnCityRaw === "boolean") isOwnCity = isOwnCityRaw
  else if (isOwnCityRaw === 1 || isOwnCityRaw === "1") isOwnCity = true

  return {
    cityId,
    title: pickString(raw.title ?? raw.name, `Город ${cityId}`),
    alias: pickString(raw.alias),
    polygon: normalizeCityPolygon(raw.polygon),
    isOwnCity,
  }
}

function unwrapLocationPointsRoot(data: unknown): Record<string, unknown> | null {
  if (typeof data === "string" && data.trim()) {
    try {
      return unwrapLocationPointsRoot(JSON.parse(data))
    } catch {
      return null
    }
  }
  if (Array.isArray(data)) return null
  if (!data || typeof data !== "object") return null
  const obj = data as Record<string, unknown>
  if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
    return obj.data as Record<string, unknown>
  }
  return obj
}

export function mapLocationPointsResponse(data: unknown): LocationPointsResponse {
  const root = unwrapLocationPointsRoot(data)
  if (!root) {
    return {
      cities: [],
      points: mapLocationPoints(data),
      foreignScans: DEFAULT_FOREIGN_SCANS,
    }
  }

  const citiesRaw = Array.isArray(root.cities) ? root.cities : []
  const cities = citiesRaw
    .map((item) =>
      normalizeCityMapItem(
        item && typeof item === "object" ? (item as RawLocationPoint) : {}
      )
    )
    .filter((city): city is CityMapItem => city != null)

  return {
    cities,
    points: mapLocationPoints(root),
    foreignScans: normalizeForeignScans(root.foreignScans ?? root.foreign_scans),
  }
}

export function normalizeScanLocationResponse(data: unknown): ScanLocationResponse {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {}
  }
  const item = data as Record<string, unknown>
  const nested =
    item.data && typeof item.data === "object" && !Array.isArray(item.data)
      ? (item.data as Record<string, unknown>)
      : null
  const source = nested ?? item

  const lootTypeRaw = pickString(source.lootType ?? source.loot_type).toUpperCase()
  const lootType =
    lootTypeRaw === "COINS" ||
    lootTypeRaw === "CHARGES" ||
    lootTypeRaw === "ENERGY" ||
    lootTypeRaw === "CARD"
      ? (lootTypeRaw as ScanLocationResponse["lootType"])
      : undefined

  const isOwnCityRaw = source.isOwnCity ?? source.is_own_city
  let isOwnCity: boolean | undefined
  if (typeof isOwnCityRaw === "boolean") isOwnCity = isOwnCityRaw
  else if (isOwnCityRaw === 0 || isOwnCityRaw === "0") isOwnCity = false
  else if (isOwnCityRaw === 1 || isOwnCityRaw === "1") isOwnCity = true

  return {
    lootType,
    amount: pickNumber(source.amount) ?? undefined,
    energy: pickNumber(source.energy) ?? undefined,
    card: source.card as ScanLocationResponse["card"],
    cooldownUntil:
      (source.cooldownUntil ?? source.cooldown_until ?? null) as
        | string
        | null
        | undefined,
    isOwnCity,
    foreignScans:
      source.foreignScans != null || source.foreign_scans != null
        ? normalizeForeignScans(source.foreignScans ?? source.foreign_scans)
        : undefined,
    passport: source.passport as ScanLocationResponse["passport"],
    message: pickString(source.message) || undefined,
  }
}
